import { Fragment, useState } from "react";
import {
  Button,
  EmptyState,
  FormModal,
  Input,
  Page,
  ResponsiveGrid,
  Select,
  Text,
} from "@marekl/adminer/components";
import { useConfirm } from "@marekl/adminer/hooks";
import { notifyError } from "@marekl/adminer/api";
import {
  useCreateAgeCategory,
  useCreateAgeTable,
  useDeleteAgeCategory,
  useDeleteAgeTable,
  useUpdateAgeCategory,
  useUpdateAgeTable,
  type AgeCategory,
  type AgeCategoryTable,
  type AgeMode,
  useDuplicateAgeTable,
} from "../../../lib/queries/rules";
import { AGE_MODE_LABELS, formatAgeRange } from "../../../lib/rulesLabels";
import { checkAgeCategories } from "../../../lib/rulesChecks";
import { RuleChecks } from "../../../components/RuleChecks";
import { ComponentCard } from "./ComponentCard";
import { isOptionalInteger, numberField, optionalNumber } from "./formValues";
import { reorderRows } from "./reorder";
import { AGE_COLOR_SLOTS, AgeBadge, AgeSwatch } from "../../../components/AgeBadge";
import tableStyles from "./componentTables.module.css";
import styles from "./agePalette.module.css";
import type { ID } from "../../../lib/id";

type ModalState =
  | { kind: "closed" }
  | { kind: "table"; table?: AgeCategoryTable }
  | { kind: "row"; tableId: ID; row?: AgeCategory };

interface TableForm {
  name: string;
  age_mode: AgeMode;
  ref_month: string;
  ref_day: string;
}

interface RowForm {
  code: string;
  name: string;
  min_age: string;
  max_age: string;
  /** A palette slot, or "" for none. */
  color: string;
}

const emptyTableForm: TableForm = {
  name: "",
  age_mode: "fixed_date",
  ref_month: "12",
  ref_day: "31",
};

const emptyRowForm: RowForm = { code: "", name: "", min_age: "", max_age: "", color: "" };

export function AgeTablesTab({
  ruleSetId,
  readOnly,
  lockedTableIds,
  tables,
}: {
  ruleSetId: ID;
  readOnly: boolean;
  /**
   * Ids of the tables whose rungs may no longer be deleted: the set is in
   * service AND a discipline builds its matrix on this table.
   *
   * Per table, not per tab. A ladder nobody references holds no
   * combinations, so deleting a rung of it drops nothing and the server
   * allows it — the same reason the table's own „Smazat" stays. Hiding the
   * button on every table would have contradicted that, and left an admin
   * able to delete a whole unused ladder but not one row of it.
   */
  lockedTableIds: Set<ID>;
  tables: AgeCategoryTable[];
}) {
  const confirm = useConfirm();
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [tableForm, setTableForm] = useState<TableForm>(emptyTableForm);
  const [rowForm, setRowForm] = useState<RowForm>(emptyRowForm);

  const createTable = useCreateAgeTable(ruleSetId);
  const updateTable = useUpdateAgeTable();
  const deleteTable = useDeleteAgeTable();
  const duplicateTable = useDuplicateAgeTable();
  const createRow = useCreateAgeCategory();
  const updateRow = useUpdateAgeCategory();
  const deleteRow = useDeleteAgeCategory();

  const close = () => {
    setModal({ kind: "closed" });
    createTable.reset();
    updateTable.reset();
    duplicateTable.reset();
    createRow.reset();
    updateRow.reset();
  };

  const openTable = (table?: AgeCategoryTable) => {
    setTableForm(
      table
        ? {
            name: table.name,
            age_mode: table.age_mode,
            ref_month: String(table.ref_month),
            ref_day: String(table.ref_day),
          }
        : emptyTableForm,
    );
    createTable.reset();
    updateTable.reset();
    setModal({ kind: "table", table });
  };

  const openRow = (tableId: ID, row?: AgeCategory) => {
    setRowForm(
      row
        ? {
            code: row.code,
            name: row.name,
            min_age: numberField(row.min_age),
            max_age: numberField(row.max_age),
            color: row.color ?? "",
          }
        : emptyRowForm,
    );
    createRow.reset();
    updateRow.reset();
    setModal({ kind: "row", tableId, row });
  };

  const submitTable = () => {
    if (modal.kind !== "table") return;
    const payload = {
      name: tableForm.name.trim(),
      age_mode: tableForm.age_mode,
      ref_month: Number(tableForm.ref_month),
      ref_day: Number(tableForm.ref_day),
    };
    if (modal.table) {
      updateTable.mutate({ id: modal.table.id, payload }, { onSuccess: close });
    } else {
      createTable.mutate(payload, { onSuccess: close });
    }
  };

  const submitRow = () => {
    if (modal.kind !== "row") return;
    const payload = {
      code: rowForm.code.trim(),
      name: rowForm.name.trim(),
      min_age: optionalNumber(rowForm.min_age),
      max_age: optionalNumber(rowForm.max_age),
      color: rowForm.color,
      sort_order: modal.row?.sort_order ?? nextSortOrder(tables, modal.tableId),
    };
    if (modal.row) {
      updateRow.mutate({ id: modal.row.id, payload }, { onSuccess: close });
    } else {
      createRow.mutate({ tableId: modal.tableId, payload }, { onSuccess: close });
    }
  };

  const move = (table: AgeCategoryTable, index: number, direction: -1 | 1) => {
    for (const { row, sort_order } of reorderRows(table.categories ?? [], index, direction)) {
      updateRow.mutate({
        id: row.id,
        payload: {
          code: row.code,
          name: row.name,
          min_age: row.min_age ?? null,
          max_age: row.max_age ?? null,
          // Carried, not omitted: the update rewrites the whole row, so a
          // field left out of a REORDER would be cleared by it. Moving a
          // rung must not discard its colour.
          color: row.color,
          sort_order,
        },
      }, { onError: (err) => notifyError(err) });
    }
  };

  const tableBusy = createTable.isPending || updateTable.isPending;
  const rowBusy = createRow.isPending || updateRow.isPending;

  return (
    <>
      <Page.SectionHeader
        title="Tabulky věkových kategorií"
        meta={
          readOnly ? undefined : (
            <Button variant="primary" onClick={() => openTable()}>
              Nová tabulka
            </Button>
          )
        }
      />
      <Text variant="muted">Věk se počítá podle nastavení tabulky.</Text>

      {tables.length === 0 ? (
        <EmptyState
          title="Žádné tabulky věkových kategorií"
          description="Založte tabulku a naplňte ji kategoriemi (DĚTI, KADET, JUNIOR, SENIOR …)."
        />
      ) : (
        tables.map((table) => (
          <Fragment key={table.id}>
            <RuleChecks warnings={checkAgeCategories(table.categories ?? [])} />
          <ComponentCard<AgeCategory>
            title={table.name}
            subtitle={
              <Text variant="muted">
                {AGE_MODE_LABELS[table.age_mode]}
                {table.age_mode === "fixed_date" && ` · k ${table.ref_day}. ${table.ref_month}.`}
              </Text>
            }
            readOnly={readOnly}
          lockDelete={lockedTableIds.has(table.id)}
            rows={table.categories ?? []}
            emptyTitle="Zatím bez kategorií"
            emptyDescription="Přidejte první věkovou kategorii."
            addLabel="Přidat kategorii"
            columns={[
              {
                key: "code",
                header: "Kód",
                tdClass: tableStyles.code,
                render: (r) => <Text variant="mono">{r.code}</Text>,
              },
              {
                // The name as it will actually look wherever the age is
                // shown, colour and all — so the admin picks the palette
                // against the thing they are editing rather than against
                // an abstract swatch.
                key: "name",
                header: "Název",
                tdClass: tableStyles.name,
                render: (r) => <AgeBadge name={r.name} color={r.color} />,
              },
              {
                key: "range",
                header: "Věk",
                tdClass: tableStyles.value,
                render: (r) => formatAgeRange(r.min_age ?? null, r.max_age ?? null),
              },
            ]}
            onEditTable={() => openTable(table)}
            onDuplicateTable={() => duplicateTable.mutate({ id: table.id }, { onError: (err) => notifyError(err) })}
            onDeleteTable={() =>
              confirm({
                title: "Smazat tabulku věkových kategorií",
                description: (
                  <>
                    Smaže se tabulka <strong>{table.name}</strong> i všechny její kategorie.
                    Tabulku, kterou používá některá disciplína, smazat nelze.
                  </>
                ),
                confirmLabel: "Smazat",
                variant: "danger",
                onConfirm: () => deleteTable.mutateAsync(table.id),
              })
            }
            onAddRow={() => openRow(table.id)}
            onEditRow={(row) => openRow(table.id, row)}
            onDeleteRow={(row) =>
              confirm({
                title: "Smazat věkovou kategorii",
                description: (
                  <>
                    Smaže se kategorie <strong>{row.name}</strong>. Ve všech disciplínách, které
                    tuto tabulku používají, zaniknou i kombinace na tomto řádku, včetně jejich
                    vlastních nastavení.
                  </>
                ),
                confirmLabel: "Smazat",
                variant: "danger",
                onConfirm: () => deleteRow.mutateAsync(row.id),
              })
            }
            onMoveRow={(index, direction) => move(table, index, direction)}
          />
          </Fragment>
        ))
      )}

      <FormModal
        open={modal.kind === "table"}
        title={modal.kind === "table" && modal.table ? "Upravit tabulku" : "Nová tabulka"}
        onClose={close}
        submitLabel="Uložit"
        busy={tableBusy}
        canSubmit={!!tableForm.name.trim()}
        error={createTable.error || updateTable.error}
        onSubmit={submitTable}
      >
        <Input
          label="Název tabulky"
          required
          placeholder="Standardní věkové kategorie"
          value={tableForm.name}
          onChange={(e) => setTableForm({ ...tableForm, name: e.target.value })}
        />
        <Select
          label="Způsob výpočtu věku"
          value={tableForm.age_mode}
          onChange={(e) =>
            setTableForm({ ...tableForm, age_mode: e.target.value as AgeMode })
          }
        >
          {Object.entries(AGE_MODE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        {tableForm.age_mode === "fixed_date" && (
          <ResponsiveGrid columns={2}>
            <Input
              label="Den"
              type="number"
              min={1}
              max={31}
              value={tableForm.ref_day}
              onChange={(e) => setTableForm({ ...tableForm, ref_day: e.target.value })}
            />
            <Input
              label="Měsíc"
              type="number"
              min={1}
              max={12}
              value={tableForm.ref_month}
              onChange={(e) => setTableForm({ ...tableForm, ref_month: e.target.value })}
              hint="Věk dosažený k tomuto dni roku soutěže."
            />
          </ResponsiveGrid>
        )}
      </FormModal>

      <FormModal
        open={modal.kind === "row"}
        title={modal.kind === "row" && modal.row ? "Upravit kategorii" : "Nová věková kategorie"}
        onClose={close}
        submitLabel="Uložit"
        busy={rowBusy}
        canSubmit={
          !!rowForm.code.trim() &&
          !!rowForm.name.trim() &&
          isOptionalInteger(rowForm.min_age) &&
          isOptionalInteger(rowForm.max_age)
        }
        error={createRow.error || updateRow.error}
        onSubmit={submitRow}
      >
        <ResponsiveGrid columns={2}>
          <Input
            label="Kód"
            required
            placeholder="JM"
            value={rowForm.code}
            onChange={(e) => setRowForm({ ...rowForm, code: e.target.value })}
          />
          <Input
            label="Název"
            required
            placeholder="JUNIOR MLADŠÍ"
            value={rowForm.name}
            onChange={(e) => setRowForm({ ...rowForm, name: e.target.value })}
          />
        </ResponsiveGrid>
        <ResponsiveGrid columns={2}>
          <Input
            label="Od (let)"
            type="number"
            min={0}
            value={rowForm.min_age}
            onChange={(e) => setRowForm({ ...rowForm, min_age: e.target.value })}
          />
          <Input
            label="Do (let)"
            type="number"
            min={0}
            value={rowForm.max_age}
            onChange={(e) => setRowForm({ ...rowForm, max_age: e.target.value })}
          />
        </ResponsiveGrid>
        <Select
          label="Barva"
          hint="Zobrazí se u věkové kategorie všude, kde je vypsaná."
          value={rowForm.color}
          onChange={(e) => setRowForm({ ...rowForm, color: e.target.value })}
        >
          {/* „Bez barvy" is a real choice, not an empty prompt: a rule set
              that never assigns one is a valid rule set, and every age
              category looked like this before the palette existed. */}
          <option value="">Bez barvy</option>
          {AGE_COLOR_SLOTS.map((slot, i) => (
            <option key={slot} value={slot}>
              {`Barva ${i + 1}`}
            </option>
          ))}
        </Select>
        {/* The whole palette, so the choice is made by looking rather than
            by reading „Barva 5" and guessing. The current pick is the one
            wearing the name. */}
        <div className={styles.palette}>
          {AGE_COLOR_SLOTS.map((slot) => (
            // eslint-disable-next-line @marekl/adminer/prefer-lib-form-controls -- the picked state must be a RING, not a face: `pressed` (0.31.0) tints the surface, and the surface here sits behind the 14px colour disc being chosen
            <button
              key={slot}
              type="button"
              className={slot === rowForm.color ? styles.slotPicked : styles.slotPick}
              aria-label={`Barva ${AGE_COLOR_SLOTS.indexOf(slot) + 1}`}
              aria-pressed={slot === rowForm.color}
              onClick={() => setRowForm({ ...rowForm, color: slot })}
            >
              <AgeSwatch color={slot} />
            </button>
          ))}
        </div>
      </FormModal>


    </>
  );
}

/** Puts a new row at the end of its table. */
function nextSortOrder(tables: AgeCategoryTable[], tableId: ID): number {
  const rows = tables.find((t) => t.id === tableId)?.categories ?? [];
  return rows.length;
}
